import type { Scene } from '@/types';

/**
 * Capítulo 3 — "El nombre que no sabías".
 *
 * Rework completo. Arco: rastreo de Sena al norte, descubrimiento de que
 * La Sombra no es lo que la Villa cree, muerte significativa (Urro),
 * manifestación del primer síntoma real en Pipo, y aparición del Cuervo
 * con una propuesta que reenmarca todo el conflicto.
 */
export const CAP3_ESCENAS: Scene[] = [
  // ===========================================================================
  //  ACTO I — El rastro
  // ===========================================================================
  {
    id: 'cap3_intro',
    capitulo: 3,
    titulo: 'El nombre que no sabías',
    background: { type: 'village', hint: 'amanecer amarillo feo, villa preparándose' },
    musica: 'forest_ambient',
    texto: [
      'Amanece con la luz más fea que viste en la villa: no gris de niebla, no dorado de otoño. Un amarillo desleído, el amarillo que tiene el papel que alguien dejó al sol por demasiado tiempo. Un color que no promete nada.',
      'La villa está despierta pero silenciosa. Nadie está hablando, pero todo el mundo está haciendo algo. Mujeres trenzando sogas nuevas. Dos viejos afilando dos cuchillos en la misma piedra, turnándose. Un chico encerando una correa de cuero. La coreografía del duelo, salvo que el muerto todavía no murió.',
      'Urro se sienta a tu lado. No te saluda. Simplemente se sienta y se queda un minuto sin decir nada, masticando algo seco que sacó del bolsillo del chaleco. Cuando termina, habla.',
      '—No me preguntes si voy. Yo voy. Vos también vas. Náyade también iría si pudiera, pero no puede: tiene una villa que sostener mientras nosotros nos ocupamos del hilo. [if flag=karn_rescatado]Orón viene detrás. Su hijo lo sostiene desde abajo: ya puede dejarlo unos días.[/if][if flag=karn_no_rescatado]Orón no viene. Anoche me dijo que todavía no está. No le insistí. Hay hombres que después de una pérdida necesitan tres meses de no hacer nada para después hacer algo enorme. Orón es de esos.[/if]',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'cap3_iniciado', valor: true },
      { kind: 'stat', stat: 'vida', delta: 25 },
      { kind: 'stat', stat: 'mana', delta: 10 },
    ],
    opciones: [
      {
        id: 'cap3_prepararse',
        texto: 'Prepararte. Lo que sea que haya al final de este camino, no lo vas a enfrentar sin comer.',
        destino: 'cap3_preparacion',
        intencion: 'neutral',
      },
      {
        id: 'cap3_preguntar_pipo',
        texto: '[emph]"¿Y Pipo?"[/emph] Todavía te duele preguntarlo, de un modo nuevo.',
        destino: 'cap3_pipo_decision',
        intencion: 'empatico',
      },
    ],
  },

  {
    id: 'cap3_pipo_decision',
    capitulo: 3,
    background: { type: 'village', hint: 'Pipo sobre una mesa, atento' },
    texto: [
      '[if flag=pipo_rescatado]Pipo está en la mesa de la cocina, comiendo un pedazo de raíz. Lo mirás. Él te mira. Ninguno dice nada, porque entre ustedes no se dice: se mira.',
      'Urro, sin levantarse, propone lo que nadie quería proponer: —Viene con vos. No lo pensás, no lo discutís. Lo llevás. La Sombra lo quiere acá. Si lo dejamos en la villa, vienen por él. Si te lo llevás, están más cerca pero vos lo tenés. Es peor, pero es mejor. A veces las dos cosas pasan a la vez.[/if][if noflag=pipo_rescatado]—Pipo no está —te recuerda Urro, despacio—. No está desde hace una semana. No hubo Pipo en esta casa. No te reproches no tenerlo. Te reprochás muchas cosas por costumbre; ésta se sale.[/if]',
    ],
    opciones: [
      {
        id: 'cap3_asumir_pipo',
        texto: '[if flag=pipo_rescatado]Agarrar a Pipo. Preparar la bandolera.[/if][if noflag=pipo_rescatado]Asentir. Seguir adelante con la ausencia.[/if]',
        destino: 'cap3_preparacion',
        intencion: 'empatico',
      },
    ],
  },

  {
    id: 'cap3_preparacion',
    capitulo: 3,
    titulo: 'Ibra, con una cosa seria',
    background: { type: 'village', hint: 'puesto de Ibra con menos mercadería que de costumbre' },
    texto: [
      'Ibra está ahí, más temprano de lo que suele, y con la mitad del inventario guardado. Vos registrás eso: un mercader que guarda mercadería antes de un viaje largo es un mercader que ya sabe algo.',
      '—Te esperaba —te dice sin mirarte—. Hoy te voy a ofrecer una cosa sola. No negocio. No me pidas otra. Si la querés, me la sacás; si no, te vas sin nada y no volvemos a hablar de esto en toda la historia.',
      'Te muestra una pluma. Una pluma negra, lisa, del tamaño del antebrazo de un chico. La pluma de un ave que no vive en estos bosques.',
      '—Es una pluma de Cuervo. Genuina. De los de él. Si la mostrás en el momento justo, te puede salvar algo. Me costó más de lo que te voy a decir. Te la doy por —y acá Ibra hace la pausa larga que vos ya le conocés— la moneda ancestral, si todavía la tenés. O por la tintura roja, si todavía la tenés. O por un recuerdo tuyo, si no tenés ninguna de las dos.',
    ],
    onEnter: [{ kind: 'flag', flagId: 'ibra_oferta_cap3', valor: true }],
    opciones: [
      {
        id: 'cap3_ibra_moneda',
        texto: 'Darle la moneda ancestral. Siempre supiste que era para pagar algo grande.',
        destino: 'cap3_camino_norte',
        intencion: 'astuto',
        condiciones: [{ kind: 'item', itemId: 'moneda_ancestral' }],
        alFallarCondicion: 'ocultar',
        efectos: [
          { kind: 'item', itemId: 'moneda_ancestral', delta: -1 },
          { kind: 'item', itemId: 'pluma_cuervo', delta: 1 },
        ],
      },
      {
        id: 'cap3_ibra_tintura',
        texto: 'Darle la tintura roja. Llevás años diciendote que no la ibas a necesitar.',
        destino: 'cap3_camino_norte',
        intencion: 'neutral',
        condiciones: [{ kind: 'item', itemId: 'tintura_roja' }],
        alFallarCondicion: 'ocultar',
        efectos: [
          { kind: 'item', itemId: 'tintura_roja', delta: -1 },
          { kind: 'item', itemId: 'pluma_cuervo', delta: 1 },
        ],
      },
      {
        id: 'cap3_ibra_recuerdo',
        texto: '[emph]"No tengo ninguna. Un recuerdo, decís."[/emph]',
        destino: 'cap3_ibra_recuerdo',
        intencion: 'empatico',
      },
      {
        id: 'cap3_ibra_rechazar',
        texto: '[emph]"No compro plumas. Me voy al camino."[/emph]',
        destino: 'cap3_camino_norte',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap3_ibra_recuerdo',
    capitulo: 3,
    background: { type: 'village', hint: 'Ibra con la pluma en la mano, muy serio' },
    texto: [
      'Ibra te mira por primera vez con los ojos entrecerrados. Es otra cara. Una cara que no te había mostrado.',
      '—Contame el primer día que la casa te quedó chica. No me digas cuándo. Contame cómo. Qué olor tenía la casa ese día. Qué hacía tu madre. Qué palabra te dijo un adulto y te marcó.',
      'Lo hacés. Hablás cinco minutos. No te acordás de haberle contado eso a nadie, nunca, y ahora se lo contás a un carpincho mercader en la puerta de una aldea que no existe en ningún mapa.',
      'Cuando terminás, Ibra asiente. Te pone la pluma en la mano. —Gracias. Eso me vale. No me preguntés por qué. Andá.',
      'No le preguntás. Te vas.',
    ],
    onEnter: [
      { kind: 'item', itemId: 'pluma_cuervo', delta: 1 },
      { kind: 'flag', flagId: 'ibra_cobró_recuerdo', valor: true },
      { kind: 'xp', cantidad: 30 },
    ],
    opciones: [
      {
        id: 'cap3_hacia_camino',
        texto: 'Hacia el norte.',
        destino: 'cap3_camino_norte',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap3_camino_norte',
    capitulo: 3,
    titulo: 'Al norte',
    background: { type: 'forest', hint: 'sendero norte, vegetación más rala, luz dura' },
    texto: [
      'El norte del Bosque Susurrante es otro bosque. Los árboles se vuelven más finos, las raíces menos profundas, el suelo más arenoso. En dos horas la vegetación ya no te tapa la cabeza; en cuatro, ya no te tapa el pecho. El cielo empieza a cobrar presencia.',
      'Urro adelante. [if flag=pipo_rescatado]Pipo en la bandolera cruzada contra tu pecho: una correa ancha de cuero tratado, un agujero para que saque la cabeza. Pesa poco, pero lo sentís pesado. No es el peso del animal: es el peso de lo que cargás con él.[/if] Vos atrás, con una mano libre para la daga y la otra para[if flag=pipo_rescatado] sostener a Pipo[/if][if noflag=pipo_rescatado] el morral[/if].',
      'A las seis horas encuentran la primera señal. Una estaca clavada en el suelo, con un paño chico atado a la punta. El paño tiene un bordado: una espiral apenas iniciada. Sena dejó el rastro. Sena quería que la siguieran.',
    ],
    opciones: [
      {
        id: 'cap3_seguir_rastro',
        texto: 'Seguir el rastro. Un paño no se pone sin querer que alguien venga.',
        destino: 'cap3_rio_nocturno',
        intencion: 'astuto',
        efectos: [{ kind: 'stat', stat: 'astucia', delta: 1 }],
      },
    ],
  },

  {
    id: 'cap3_rio_nocturno',
    capitulo: 3,
    background: { type: 'river', hint: 'río nocturno con bote de madera amarrado' },
    texto: [
      'De noche, llegan a un río. Ni ancho ni angosto: incómodo. De los ríos que a uno le dan lástima porque no encuentran su tamaño. En la orilla hay un bote de madera con dos remos rotos. Sena lo usó para cruzar. Lo dejó amarrado con una intención clarísima: volvé por acá si me seguís.',
      'Urro olfatea los remos. —Tres días atrás —dice—. Tiene ventaja. Pero no quería perdernos. Eso también es un dato.',
      'Cruzan. Del otro lado hay una aldea humana. Eso te sorprende porque no sabías que había aldeas humanas en esta dirección. Tampoco sabés si está habitada.',
    ],
    opciones: [
      {
        id: 'cap3_aldea_abandonada',
        texto: 'Acercarse. Con cuidado.',
        destino: 'cap3_aldea_abandonada',
        intencion: 'astuto',
      },
    ],
  },

  // ===========================================================================
  //  ACTO II — El pueblo abandonado
  // ===========================================================================
  {
    id: 'cap3_aldea_abandonada',
    capitulo: 3,
    titulo: 'La aldea sin nadie',
    background: { type: 'ruins', hint: 'pueblo humano abandonado, puertas abiertas' },
    texto: [
      'No está abandonada: está [glow color=mystic]vaciada[/glow]. Las puertas abiertas. Las mesas puestas. Un plato con comida que ya crió una cáscara gris. Una cuna vacía con la manta todavía con forma de bebé. Los habitantes se fueron con la urgencia que se va el que cree que algo viene. Pero no pueden haberse ido todos al mismo tiempo. Alguien los llamó.',
      'Urro entra sin mirarte. Se detiene en medio de la plaza. Husmea. Gira en círculos cortos.',
      '—Incendios —dice—. Pero no en este pueblo. Los olores vienen de más al norte. Alguien acá esperó mucho una señal. Cuando la señal llegó, salieron todos.',
      'Vos recordás a Vega: [emph]"los incendios no son destrucción. Son señales."[/emph]',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'vio_aldea_humana_vaciada', valor: true },
      { kind: 'xp', cantidad: 30 },
    ],
    opciones: [
      {
        id: 'cap3_explorar_aldea',
        texto: 'Explorar. Alguien tuvo que dejar algo por descuido.',
        destino: 'cap3_explorar_aldea',
        intencion: 'astuto',
      },
      {
        id: 'cap3_seguir_norte',
        texto: 'No detenerse. La pista es hacia el norte.',
        destino: 'cap3_bosque_muerto',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap3_explorar_aldea',
    capitulo: 3,
    background: { type: 'ruins', hint: 'interior de casa humana, papeles sobre mesa' },
    texto: [
      'En la casa principal hay una mesa con papeles. Son recibos. [glow color=ember]Pagos de contratación.[/glow] Facturas escritas a mano por un hombre al que le piden, en letra chica, "doce cazadores por quince días, con trampas nuevas, ver sección 7".',
      'El firmante es una empresa: "Sociedad Forestal del Sur". Una dirección al pie, en un pueblo grande que vos conocés. La ciudad en la que pasaste los primeros años antes de venir al bosque.',
      'Guardás un recibo. No sabés para qué te va a servir. Sabés que va a servir.',
      '[if skill=ojo_cuervo]Una cosa más: en los márgenes del papel hay una firma distinta, garabateada, con tinta de otro color. La misma caligrafía que viste en la libreta del cazador al que interceptaste en el Cap. 1. La misma mano, firmando ambos. [emph]El Cuervo se pasea por los dos lados[/emph].[/if]',
    ],
    onEnter: [
      { kind: 'item', itemId: 'recibo_forestal_sur', delta: 1 },
      { kind: 'flag', flagId: 'sabe_nombre_empresa', valor: true },
      { kind: 'xp', cantidad: 40 },
    ],
    opciones: [
      {
        id: 'cap3_hacia_norte_tras_aldea',
        texto: 'Seguir. El norte se está quedando corto.',
        destino: 'cap3_bosque_muerto',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap3_bosque_muerto',
    capitulo: 3,
    titulo: 'Tierras Quemadas',
    background: { type: 'fire', hint: 'bosque carbonizado reciente, humo lejano' },
    texto: [
      'A seis kilómetros de la aldea abandonada, el bosque simplemente deja de ser bosque. Primero son árboles aislados carbonizados: uno sí, uno no, uno sí. Después son todos. Después es una llanura negra que se extiende hasta donde el horizonte se empieza a cagar la vista con el humo.',
      'Las [emph]Tierras Quemadas[/emph]. Les pusieron ese nombre los carpinchos antes de que el fuego llegara. Ahora el nombre encaja.',
      '[if flag=pipo_rescatado]Pipo, en la bandolera, empieza a temblar. No de frío: de otra cosa. Cierra los ojos. Los abre. Los ojos que abre no son sus ojos. Son ojos blancos, completamente blancos, sin iris ni pupila. Cuatro segundos así. Vos no respirás en esos cuatro segundos. Al quinto, vuelve a ser Pipo. Tiene hambre, te mira pidiendo. Vos no olvidás los cuatro segundos. Vas a vivir el resto de tu vida acordándote de los cuatro segundos.[/if]',
      'Urro registra que algo pasó. No pregunta. Él también se pone a temblar, apenas. Los carpinchos viejos tienen una sensibilidad para estas cosas que te hace pensar en antenas.',
    ],
    onEnter: [
      { kind: 'unlockLocation', locacionId: 'tierras_quemadas' },
      { kind: 'flag', flagId: 'vio_pipo_sintoma', valor: true },
      { kind: 'xp', cantidad: 50 },
    ],
    opciones: [
      {
        id: 'cap3_avanzar_tierras_quemadas',
        texto: 'Avanzar. No hay nada más que hacer.',
        destino: 'cap3_campamento_sombra',
        intencion: 'valiente',
      },
    ],
  },

  // ===========================================================================
  //  ACTO III — El campamento
  // ===========================================================================
  {
    id: 'cap3_campamento_sombra',
    capitulo: 3,
    titulo: 'La otra aldea',
    background: { type: 'cave', hint: 'valle rocoso con un asentamiento pequeño y humilde' },
    texto: [
      'Bajás a un valle chico, escondido entre dos cerros que no figuran en los mapas de Urro. Urro lo reconoce de golpe.',
      '—Nunca vine. Sabía que existía. Nos dijeron que era una guarida.',
      'No es una guarida. Es una aldea. Treinta carpinchos oscuros moviéndose con la normalidad aburrida de una aldea cualquiera: dos viejas remendando una red, un chico limpiando pescado al sol, una huerta con porotos y calabaza cuidada con cariño, un fogón con olor a caldo que vos conocés y te da hambre.',
      'Esto es lo que más te descoloca de toda la aventura hasta ahora. Los [shake]malos[/shake] no están tramando. Están [emph]viviendo[/emph]. Los malos se portan como los tuyos. Los malos son los tuyos vistos desde otro lado.',
    ],
    opciones: [
      {
        id: 'cap3_observar_antes',
        texto: 'Observar antes de entrar. Una aldea que parece aldea tiene tripa; hay que encontrarla.',
        destino: 'cap3_ver_sena',
        intencion: 'astuto',
      },
      {
        id: 'cap3_entrar_directo',
        texto: 'Entrar directo. La diplomacia aveces funciona mejor sin disfraces.',
        destino: 'cap3_entrada_directa',
        intencion: 'valiente',
      },
    ],
  },

  {
    id: 'cap3_ver_sena',
    capitulo: 3,
    background: { type: 'cave', hint: 'Sena visible en la aldea, peinando a un niño' },
    texto: [
      'A los quince minutos de observación, la ves. Sena. Está sentada en un banco bajo un árbol raquítico, peinándole el pelaje a un carpincho chico —cinco, seis años como mucho—. Le habla despacio. El chico se ríe. Sena se ríe con él.',
      'Ese chico es su hermano. Lo entendés en el modo en que ella lo toca. No es una cuidadora: es una hermana que recuperó a alguien después de demasiado tiempo.',
      'Urro te mira. Vos lo mirás a Urro. Los dos entienden lo mismo al mismo tiempo: [glow color=mystic]acá no hay rescate de Sena[/glow]. Acá hay otra cosa. Algo más complicado.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'vio_sena_hermano', valor: true },
      { kind: 'xp', cantidad: 40 },
    ],
    opciones: [
      {
        id: 'cap3_acercarse_sena',
        texto: 'Acercarse a pie, sin armas, con las manos visibles.',
        destino: 'cap3_sena_reconoce',
        intencion: 'empatico',
      },
      {
        id: 'cap3_infiltrar_sena',
        texto: 'Entrar por atrás. Agarrarla sola, hablarle sin testigos.',
        destino: 'cap3_sena_reconoce',
        intencion: 'astuto',
        condiciones: [{ kind: 'stat', stat: 'astucia', minimo: 4 }],
        alFallarCondicion: 'atenuar',
      },
    ],
  },

  {
    id: 'cap3_entrada_directa',
    capitulo: 3,
    background: { type: 'cave', hint: 'plaza central de la aldea de La Sombra, todos mirándote' },
    texto: [
      'Bajás al valle. Camino abierto. No te escondés. A los cuarenta pasos, todos los adultos dejan lo que están haciendo y te miran. Nadie saca arma. Nadie grita. Todos miran.',
      'De una choza sale Sena. Tiene la cara de quien ya sabía que ibas a venir. No sonríe ni se pone a la defensiva. Camina hacia vos con los brazos a los costados, el paso lento.',
      'A diez metros se detiene. Su hermano chico se asoma detrás de ella, atado al pelaje de su cadera como una lapa.',
      '—Llegaste antes de lo que pensaba —te dice—. Necesitamos hablar.',
    ],
    opciones: [
      {
        id: 'cap3_escuchar_sena',
        texto: 'Escuchar.',
        destino: 'cap3_sena_explica',
        intencion: 'empatico',
      },
    ],
  },

  {
    id: 'cap3_sena_reconoce',
    capitulo: 3,
    titulo: 'Sena',
    background: { type: 'cave', hint: 'Sena sentada, te recibe sin asombro' },
    texto: [
      'Sena no se asombra cuando la alcanzás. Ni siquiera cuando Urro se pone a su costado. El chico —su hermano, Tebo, te dirá después— se esconde detrás de ella. No por miedo. Por timidez.',
      '—Le pedí a Náyade que no mandara a nadie a buscarme —te dice—. Le dije que me iba de mi propia voluntad. Obviamente no le creyó. Obviamente mandó a los dos que más confía. Gracias por venir. No lo digo en serio. Lo digo porque corresponde.',
      '—Sentate, Franco. Voy a explicarte el lío. Tu capacidad de perdonarme va a depender de cuánto te creas de lo que voy a decirte.',
    ],
    opciones: [
      {
        id: 'cap3_escuchar_sena_privado',
        texto: 'Sentarte.',
        destino: 'cap3_sena_explica',
        intencion: 'empatico',
      },
    ],
  },

  {
    id: 'cap3_sena_explica',
    capitulo: 3,
    titulo: 'La verdad de Sena',
    background: { type: 'cave', hint: 'Sena con Tebo en el regazo' },
    texto: [
      '—Nací acá, Franco. No nací en la villa de Náyade. Nací en esta aldea que vos pensás "la guarida de La Sombra". Mi familia vivió acá hasta que un grupo del Clan del Río —hace diez años— nos atacó, nos quemó las chozas, dispersó a los míos. A mi madre la mataron. Mi hermano y yo nos salvamos. A mí me llevó Náyade, no sabiendo quiénes éramos, y me crió como cría huérfana. A Tebo lo encontró Tizón —el que ustedes llaman el líder de La Sombra— y lo crió acá.',
      '—Me enteré hace dos años. Tebo me contactó por un mensajero. Me pedía que volviera. Yo no podía: amaba a Náyade, amaba la villa. Pero Tebo es mi hermano, y Tebo es chico. Entonces hice lo único que pude: envenené el agua para que los carpinchos se fueran al sur, donde estaban protegidos por un acuerdo viejo entre esta aldea y una tribu amiga del sur. Creía que los mandaba a un refugio. No mato a nadie: los desplazaba.',
      '—Pecora fue un accidente. A ella no tenía que haberle tocado. Yo no puse veneno en su pozo. Alguien más lo hizo. Y yo no sé quién.',
      'Te mira fijo. —Lo que te estoy diciendo es cierto. Podés verificarlo con Vínculo Empático si lo tenés.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'sena_explicó', valor: true },
      { kind: 'xp', cantidad: 50 },
    ],
    opciones: [
      {
        id: 'cap3_usar_vinculo_sena',
        texto: 'Apoyarle la mano en la frente. Usar Vínculo Empático para verificar.',
        destino: 'cap3_vinculo_sena',
        intencion: 'astuto',
        condiciones: [{ kind: 'spell', hechizoId: 'vinculo_empatico' }],
        alFallarCondicion: 'atenuar',
      },
      {
        id: 'cap3_creerle_sena',
        texto: '[emph]"Te creo sin tocarte. Contame el resto."[/emph]',
        destino: 'cap3_sena_resto',
        intencion: 'empatico',
        efectos: [
          { kind: 'stat', stat: 'empatia', delta: 1 },
          { kind: 'flag', flagId: 'creyó_a_sena_sin_verificar', valor: true },
        ],
      },
      {
        id: 'cap3_desconfiar_sena',
        texto: '[emph]"No tengo por qué creerte. Venís con nosotros a la villa y hablás ahí."[/emph]',
        destino: 'cap3_sena_negociar',
        intencion: 'astuto',
      },
    ],
  },

  {
    id: 'cap3_vinculo_sena',
    capitulo: 3,
    background: { type: 'mystical', hint: 'visión, imagen fragmentada de aldea ardiendo' },
    texto: [
      'Tocás la frente de Sena. La visión se arma sola: una niña chica, seis años, corriendo entre chozas que arden. Una voz grave que te arranca la mano y te arrastra: [whisper]"No mires atrás, Sena. Nunca mires atrás."[/whisper] Una chimenea cayendo encima de un carpincho que vos reconocés sin haberlo conocido: el padre de Sena.',
      'Y otra imagen, más reciente, superpuesta: Sena arrodillada junto a un pozo vertiendo un frasco. El frasco lo reconocés: es el mismo que tenía Rami. Y atrás, en la sombra del pozo, una segunda figura que Sena no ve: alguien le alcanzó el frasco. Alguien de la Villa.',
      'La visión termina con la pregunta flotando: ¿quién se lo dio?',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'sena_confirmó_verdad', valor: true },
      { kind: 'flag', flagId: 'hay_segundo_en_villa', valor: true },
      { kind: 'xp', cantidad: 60 },
    ],
    opciones: [
      {
        id: 'cap3_sena_seguir_post_vinculo',
        texto: '[emph]"Te creo. Contame lo que falta."[/emph]',
        destino: 'cap3_sena_resto',
        intencion: 'empatico',
      },
    ],
  },

  {
    id: 'cap3_sena_resto',
    capitulo: 3,
    background: { type: 'cave', hint: 'Sena hablando, Tebo dormido en su regazo' },
    texto: [
      '—Tizón está enfermo —te dice Sena—. No el tipo de enfermo que se cura. El tipo de enfermo que ya decidió. Su sucesor no está decidido. Hay una facción acá —chica, pero chillona— que quiere la guerra total contra la Villa. Otra, la mayoritaria, quiere un acuerdo. Yo pertenezco a la que quiere el acuerdo.',
      '—Pero hay una cosa más, Franco. Y esta cosa no me la enteré yo sola. Es lo que te van a contar Tizón y el Cuervo si los dejás hablar. Esta aldea —La Sombra—, la Villa del Clan del Río, todos los clanes: estamos peleando la pelea equivocada. Lo que hay adentro de Pipo es más viejo que nosotros. Vega se lo dijo. Tizón lo sabe desde hace quince años. El Cuervo lo confirmó ayer, en persona, acá.',
      '—El Cuervo te quiere ver. Está adentro, en la choza grande. Te va a proponer algo. Escuchalo antes de decidir. Yo no te digo qué hacer. Pero escuchalo.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'sena_mencionó_cuervo_ahí', valor: true },
      { kind: 'xp', cantidad: 30 },
    ],
    opciones: [
      {
        id: 'cap3_ir_al_cuervo',
        texto: 'Ir a la choza grande.',
        destino: 'cap3_choza_cuervo',
        intencion: 'valiente',
      },
    ],
  },

  {
    id: 'cap3_sena_negociar',
    capitulo: 3,
    background: { type: 'cave', hint: 'Sena frunce el ceño, asiente' },
    texto: [
      '—Es justo —dice Sena—. Vengo con ustedes. Tebo viene conmigo: no lo dejo. Pero antes de irnos de acá, vení a la choza grande. El Cuervo está adentro. Te espera. Si no hablás con él, todo esto que te conté no se sostiene. Si hablás con él, tal vez vuelvas con más cosas de las que veniste a buscar.',
    ],
    opciones: [
      {
        id: 'cap3_aceptar_al_cuervo',
        texto: 'Ir.',
        destino: 'cap3_choza_cuervo',
        intencion: 'neutral',
      },
    ],
  },

  // ===========================================================================
  //  ACTO IV — El Cuervo, otra vez
  // ===========================================================================
  {
    id: 'cap3_choza_cuervo',
    capitulo: 3,
    titulo: 'El Cuervo, en su casa',
    background: { type: 'cave', hint: 'choza grande, fogón central, el Cuervo sentado' },
    texto: [
      'La choza grande tiene una sola pieza. El Cuervo está sentado junto al fogón, más viejo que la última vez que lo viste. Las cicatrices en el hocico parecen nuevas, lo cual es imposible. Tal vez no envejeció: tal vez se enfermó de algo que le saca carne.',
      '—Franco. Te dije que volveríamos a hablar —dice, con la voz más baja que antes—. No esperaba que fuera tan pronto. Mejor para los dos. No tengo demasiado tiempo.',
      '—Sentate. Te voy a contar lo que no quise contarte en el Santuario. Si al terminar querés matarme, la pluma la tenés: se usa para hacerme salir sin pelear. No va a hacer falta. Pero por las dudas.',
      '[if item=pluma_cuervo]Te toca la pluma en el bolsillo. La siente Él, desde la distancia. Asiente con un ojo, apenas.[/if]',
    ],
    opciones: [
      {
        id: 'cap3_dejar_hablar',
        texto: 'Sentarte. Escuchar.',
        destino: 'cap3_cuervo_revelacion',
        intencion: 'empatico',
      },
    ],
  },

  {
    id: 'cap3_cuervo_revelacion',
    capitulo: 3,
    titulo: 'Lo que hay adentro de Pipo',
    background: { type: 'cave', hint: 'Cuervo hablando despacio, brasas bajas' },
    texto: [
      '—Hace quince años, Tizón y yo desertamos de los Guardianes del Fuego. No por ideología. Por una excavación que hizo un joven curioso de los nuestros en las Tierras Quemadas, cuando todavía no estaban quemadas. Encontró algo bajo tierra. Una piedra. Más vieja que nosotros. Más vieja que los carpinchos. Tal vez más vieja que el bosque mismo. Tenía un grabado: una espiral.',
      '—La piedra hablaba. No por la boca, porque no tiene. Hablaba a los oídos de los que la tocaban. Tizón la tocó. Yo la toqué. Nos dijo —o nos sugirió, no hay un verbo adecuado— que tenía hambre. Que el hambre se podía satisfacer con un recipiente vivo. Que el recipiente ideal era un carpincho con sangre de Guardián.',
      '—Tizón nunca le dijo a nadie. Yo tampoco. Enterramos la piedra otra vez. Juramos no hablar. Pero la piedra empezó a llamar, Franco. Empezó a provocar los incendios donde se sentía observada. Los incendios son las llamadas de esa cosa. Cuando queme bastante, va a salir. Cuando salga, va a elegir.',
      '—Pipo, Franco. Pipo es el recipiente que está calibrado desde antes de nacer. Si Pipo muere sin haberse activado, la cosa no tiene dónde ir y se queda dormida mil años más. Si Pipo muere activándose —es decir, si algo lo toca en el momento equivocado— la cosa entra, lo usa. Si la cosa entra en Pipo, ustedes lo enfrentan y lo matan, la cosa salta a otro. La única forma de terminarla es la primera: Pipo se muere antes de que la cosa llegue al momento de elegir.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'sabe_origen_piedra', valor: true },
      { kind: 'flag', flagId: 'sabe_pipo_destino', valor: true },
      { kind: 'xp', cantidad: 100 },
    ],
    opciones: [
      {
        id: 'cap3_cuervo_reaccion_negar',
        texto: '[emph]"No me estás pidiendo que mate a Pipo."[/emph]',
        destino: 'cap3_cuervo_oferta',
        intencion: 'hostil',
      },
      {
        id: 'cap3_cuervo_reaccion_preguntar',
        texto: '[emph]"¿Cuánto falta para que la cosa llegue al momento?"[/emph]',
        destino: 'cap3_cuervo_oferta',
        intencion: 'astuto',
      },
    ],
  },

  {
    id: 'cap3_cuervo_oferta',
    capitulo: 3,
    titulo: 'La oferta',
    background: { type: 'cave', hint: 'Cuervo serio, leña crujiendo' },
    texto: [
      '—No te estoy pidiendo que mates a Pipo —dice el Cuervo, sin énfasis—. Te estoy pidiendo que entiendas que esa es una de las opciones. Las otras son: encontrar una forma de desviar a la cosa hacia otro recipiente —un carpincho adulto con sangre de Guardián; no hay, salvo yo mismo—; o contenerla en Pipo el máximo tiempo posible, creando un objeto que la distraiga. Eso último es lo que Tizón llevaba quince años investigando. No lo terminó.',
      '—Si te unís a nosotros, terminamos el objeto contenedor. Quizá salvamos al pibe. Quizá no. Si te unís a Náyade sin decirle nada, la Villa pelea la guerra equivocada y pierde tiempo. Si le decís a Náyade lo que te conté, ella va a decidir: creerte o no creerte. Si te cree, probablemente te pida que vuelvas acá a terminar el trabajo con nosotros. Si no te cree, pierde dos semanas más y la cosa se acerca.',
      '—Esto no es una proposición para que me digas sí o no acá. Es una proposición para que te la lleves, la pienses, y cuando tengas respuesta me la traigas. Hay tiempo. Hay menos del que quisiera, pero hay.',
    ],
    opciones: [
      {
        id: 'cap3_cuervo_aceptar_pensar',
        texto: '[emph]"Lo pienso. Vuelvo con respuesta."[/emph]',
        destino: 'cap3_salida_alarma',
        intencion: 'astuto',
        efectos: [{ kind: 'flag', flagId: 'cuervo_oferta_pendiente', valor: true }],
      },
      {
        id: 'cap3_cuervo_rechazar',
        texto: '[emph]"No me pongo del lado de nadie que proponga matar a Pipo. Ni siquiera como opción."[/emph]',
        destino: 'cap3_salida_alarma',
        intencion: 'hostil',
        efectos: [
          { kind: 'reputation', facciónId: 'sombra', delta: -15 },
          { kind: 'flag', flagId: 'rechazó_cuervo_cap3', valor: true },
        ],
      },
    ],
  },

  // ===========================================================================
  //  ACTO V — La caída
  // ===========================================================================
  {
    id: 'cap3_salida_alarma',
    capitulo: 3,
    titulo: 'La alarma',
    background: { type: 'cave', hint: 'afuera de la choza, gritos' },
    texto: [
      'Salís con Urro, Sena y Tebo. Todavía no procesaste la mitad de lo que escuchaste. No te va a dar tiempo.',
      'Del otro lado del valle, por la cresta oeste, bajan veinte cazadores humanos armados con rifles de pólvora negra. No son de La Sombra: son de la empresa cuyo recibo guardás en el bolsillo. La "Sociedad Forestal del Sur". Decidieron terminar el trabajo en luna de ahora.',
      'Urro te agarra el brazo. —Franco. Salida única. El paso norte. Solo cabe uno por vez. Alguien se queda tapando. Ustedes cuatro no tienen tiempo de discutir quién. Lo elige quien está más cerca del paso.',
      'Él está más cerca del paso.',
    ],
    opciones: [
      {
        id: 'cap3_aceptar_sacrificio',
        texto: 'No podés negarlo. La decisión es de él, no tuya.',
        destino: 'cap3_muerte_urro',
        intencion: 'empatico',
      },
      {
        id: 'cap3_ofrecer_lugar',
        texto: '[emph]"Dejame a mí. Vos conocés el bosque, ellos no pueden sin vos."[/emph]',
        destino: 'cap3_urro_responde_ofrecimiento',
        intencion: 'valiente',
      },
    ],
  },

  {
    id: 'cap3_urro_responde_ofrecimiento',
    capitulo: 3,
    background: { type: 'cave', hint: 'Urro te agarra de la cara con las dos manos' },
    texto: [
      'Urro te agarra la cara con las dos manos. Es la primera vez que te toca así. Es la última.',
      '—Franco. Vos cargás al pibe. Yo cargo esto. La diferencia es que vos cargás algo que recién empieza y yo cargo algo que se termina. No pelées la decisión. No honra mi muerte. Andá.',
      'Te empuja hacia atrás. Vos te das vuelta antes de caer. Corrés. Sena lleva a Tebo. El paso norte se angosta a tres metros. Tres metros que recorrés sin respirar.',
    ],
    opciones: [
      {
        id: 'cap3_cruzar_paso',
        texto: 'Cruzar.',
        destino: 'cap3_muerte_urro',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap3_muerte_urro',
    capitulo: 3,
    titulo: 'Urro',
    background: { type: 'fire', hint: 'vista desde el paso norte, polvo y gritos abajo' },
    texto: [
      'Del otro lado del paso están a salvo por un minuto. Tiempo justo para darte vuelta y mirar.',
      'Urro se planta en el cuello del paso, carpincho viejo contra una fila de humanos con armas de pólvora. No usa nada: no tiene nada. Solo está. Tapa la salida con el cuerpo. El primer tiro le atraviesa un hombro. El segundo le parte el muslo. El tercero no lo vas a escuchar porque Sena te tira del brazo con una fuerza que no sabías que tenía.',
      '—No mirés, Franco. No lo mirés. No le gustaría.',
      'Mirás igual. Una fracción de segundo. Urro está de rodillas y está sonriendo. Eso no es una metáfora. Un carpincho viejo, al que le rompieron la pierna y el hombro, está sonriendo. Sonríe hacia vos, un segundo, y cierra los ojos.',
      '[shake]La salva final la oís aunque querés no oírla.[/shake]',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'urro_murió', valor: true },
      { kind: 'stat', stat: 'vinculo', delta: -2 },
      { kind: 'xp', cantidad: 80 },
    ],
    opciones: [
      {
        id: 'cap3_seguir_fuga',
        texto: 'Seguir corriendo. Duelo después.',
        destino: 'cap3_fuga_norte',
        intencion: 'neutral',
      },
    ],
  },

  {
    id: 'cap3_fuga_norte',
    capitulo: 3,
    background: { type: 'forest', hint: 'bosque espeso, cuatro caminantes perseguidos' },
    texto: [
      'Corren dos horas. Los cazadores se pierden en el paso porque Urro los demoró lo suficiente. Cuando al fin paran, es al borde de un arroyo. Sena le da agua a Tebo. Vos te sentás con la espalda contra un árbol. [if flag=pipo_rescatado]Pipo te mira desde la bandolera. Tiene los ojos suyos otra vez. Agarra tu mano con su manito chica. Pipo acaba de tener cuatro años y de perder un abuelo al que conocía de otra manera. No tenés palabras para eso. Ninguna.[/if]',
      'Sena se acerca.',
      '—Tizón murió esta madrugada —dice, bajito—. Mientras nosotros hablábamos con el Cuervo. No sé si vos lo sabés: ya no hay aldea de La Sombra con líder. Lo que quede de ella va a partirse. La facción de la guerra va a ganar. El Cuervo ahora es presidente de un cadáver.',
      '—No te puedo pedir que vuelvas conmigo a la villa. Tengo que ir al sur con Tebo: su tribu es nuestra tribu. Si Náyade me perdona, vuelvo un día. Si no, bueno. Así son las vueltas.',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'tizón_murió', valor: true },
      { kind: 'flag', flagId: 'sena_al_sur', valor: true },
      { kind: 'xp', cantidad: 50 },
    ],
    opciones: [
      {
        id: 'cap3_despedirse_sena',
        texto: 'Despedirte. Dejarla ir.',
        destino: 'cap3_regreso_villa',
        intencion: 'empatico',
        efectos: [{ kind: 'stat', stat: 'empatia', delta: 1 }],
      },
      {
        id: 'cap3_pedirle_volver',
        texto: '[emph]"No te vayas. Náyade te necesita para entenderte. Es la única forma."[/emph]',
        destino: 'cap3_sena_vuelve',
        intencion: 'empatico',
        condiciones: [{ kind: 'stat', stat: 'empatia', minimo: 4 }],
        alFallarCondicion: 'atenuar',
      },
    ],
  },

  {
    id: 'cap3_sena_vuelve',
    capitulo: 3,
    background: { type: 'forest', hint: 'Sena mirándote con Tebo en brazos' },
    texto: [
      'Sena te mira largo. Después mira a Tebo. Tebo la mira. El chico entiende más de lo que su edad permite.',
      '—Vengo —dice—. Tebo también viene. Si Náyade se niega, nos vamos juntos al sur. Pero quiero escuchar a Náyade primero. Merece esa chance.',
    ],
    onEnter: [{ kind: 'flag', flagId: 'sena_vuelve_con_tebo', valor: true }],
    opciones: [
      {
        id: 'cap3_volver_todos_villa',
        texto: 'Volver los cuatro.',
        destino: 'cap3_regreso_villa',
        intencion: 'valiente',
      },
    ],
  },

  // ===========================================================================
  //  ACTO VI — El nombre
  // ===========================================================================
  {
    id: 'cap3_regreso_villa',
    capitulo: 3,
    titulo: 'El regreso',
    background: { type: 'village', hint: 'villa a la anochecer, Náyade esperando' },
    texto: [
      'Llegan a la villa tres días después. No hay recibimiento festivo. Náyade los recibe sola, sentada en la pasarela central, con la espalda más recta que nunca y la cara sin gesto.',
      '—Urro —dice, cuando estás a diez pasos. No es pregunta. Ya lo sabía. Los viejos sienten las muertes de los otros viejos.',
      'Asentís. Ella cierra los ojos un segundo. Cuando los abre, mira a [if flag=sena_vuelve_con_tebo]Sena y Tebo[/if][if noflag=sena_vuelve_con_tebo]el lugar vacío atrás tuyo[/if].',
      '[if flag=sena_vuelve_con_tebo]—Entren a la choza. Los cuatro. Hay mate. Vamos a hablar mucho esta noche. Esta noche nadie va a dormir y eso está bien. Las muertes que importan se pagan con vigilia.[/if][if noflag=sena_vuelve_con_tebo]—Entrá, Franco. Hay mate. Vamos a hablar mucho esta noche. Esta noche yo no voy a dormir. Las muertes que importan se pagan con vigilia.[/if]',
    ],
    opciones: [
      {
        id: 'cap3_noche_mate',
        texto: 'Entrar. Contar.',
        destino: 'cap3_fin_cap3',
        intencion: 'empatico',
      },
    ],
  },

  {
    id: 'cap3_fin_cap3',
    capitulo: 3,
    titulo: 'Fin del Capítulo 3',
    background: { type: 'village', hint: 'choza con fogón bajo, mate entre todos' },
    texto: [
      'Hablan hasta el amanecer. Vos contás lo de Urro con la precisión del que tiene que hacerlo ya o no lo hace nunca. Contás lo del Cuervo. Contás lo de la piedra.',
      'Náyade escucha. No interrumpe. Cuando terminás, se queda callada dos minutos largos.',
      '—No sé si el Cuervo te mintió —dice al fin—. Tampoco sé si te dijo la verdad. Lo que sí sé es que mañana te voy a pedir que vayas al último lugar de esta historia: al Corazón de la Civilización. Ahí están los registros de los primeros Guardianes. Si hubo una piedra enterrada, lo sabían. Si la taparon, sabrán por qué. [if flag=pipo_rescatado]A Pipo lo llevás. Lo que le empezó a pasar hoy no es ajeno a esto. Cerca del Corazón puede que se manifieste con más claridad, para bien o para mal. Hay que saber.[/if][if noflag=pipo_rescatado]A Pipo no lo tenés, lo sé. Pero lo que anida en él está siguiendo un calendario, esté donde esté. El Corazón te va a decir en qué día de ese calendario estamos.[/if]',
      '—Dormí lo que puedas. Mañana empieza algo que no sé cómo termina.',
      '[whisper](Fin del Capítulo 3 — "El nombre que no sabías". Seguimos en el Capítulo 4.)[/whisper]',
    ],
    onEnter: [
      { kind: 'flag', flagId: 'cap3_completado', valor: true },
      { kind: 'unlockLocation', locacionId: 'corazon_civilizacion' },
      { kind: 'xp', cantidad: 150 },
    ],
    opciones: [
      {
        id: 'cap3_entrar_cap4',
        texto: 'Entrar al Capítulo 4.',
        destino: 'cap4_intro',
        intencion: 'neutral',
      },
    ],
  },
];
